import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, PencilSimpleLine, MapTrifold } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";

// A living archive night-sky hero — matches the approved editorial mock.
// SVG-first illustration: gradient sky, sun + moon, mountains, water horizon,
// with centered display copy and two primary CTAs.

function useClockAt(zone = "America/Los_Angeles") {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const opts = { timeZone: zone, hour: "numeric", minute: "2-digit", hour12: true };
  const dateOpts = { timeZone: zone, weekday: "long", year: "numeric", month: "long", day: "numeric" };
  return {
    time: now.toLocaleTimeString("en-US", opts),
    date: now.toLocaleDateString("en-US", dateOpts),
  };
}

export default function KineticHero() {
  const { time, date } = useClockAt("America/Los_Angeles");

  return (
    <section
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#FDF6E7] text-[color:var(--np-text,#241209)]"
    >
      {/* --- SVG illustrated morning-sky backdrop (light theme) --- */}
      <div className="absolute inset-0">
        <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
          <defs>
            <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#DCEEF9" />
              <stop offset="45%" stopColor="#FDF3E0" />
              <stop offset="70%" stopColor="#FCE8C8" />
              <stop offset="100%" stopColor="#DCEBDD" />
            </linearGradient>
            <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#CFE4EC" />
              <stop offset="100%" stopColor="#AECFDC" />
            </linearGradient>
            <radialGradient id="sunGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#FFC873" stopOpacity="0.9" />
              <stop offset="60%" stopColor="#E4A15C" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#E4A15C" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="moonGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.7" />
              <stop offset="60%" stopColor="#C9BED8" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#C9BED8" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="risingSun" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#FFB870" stopOpacity="0.85" />
              <stop offset="60%" stopColor="#B85F2A" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#B85F2A" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Sky */}
          <rect width="1600" height="700" fill="url(#skyGrad)" />

          {/* Soft drifting birds */}
          {Array.from({ length: 12 }).map((_, i) => {
            const cx = (i * 173) % 1500 + 40;
            const cy = (i * 67) % 320 + 60;
            return <path key={i} d={`M${cx} ${cy} q6 -7 12 0 q6 -7 12 0`} stroke="#3E5A4C" strokeWidth="2" fill="none" opacity="0.45" />;
          })}

          {/* Sun (top-left) with glow */}
          <circle cx="240" cy="220" r="180" fill="url(#sunGlow)" />
          <circle cx="240" cy="220" r="58" fill="#F0A44E" />
          <circle cx="240" cy="220" r="58" fill="url(#sunGlow)" />

          {/* Pale morning moon (top-right) */}
          <circle cx="1360" cy="200" r="120" fill="url(#moonGlow)" />
          <g>
            <circle cx="1360" cy="200" r="46" fill="#F6F1E4" opacity="0.9" />
            <circle cx="1378" cy="192" r="42" fill="#DCEEF9" opacity="0.85" />
          </g>

          {/* Distant planet (mid-left) */}
          <circle cx="200" cy="470" r="46" fill="#EFE6D8" opacity="0.9" />
          <ellipse cx="200" cy="470" rx="70" ry="8" fill="#A08B74" opacity="0.45" />

          {/* Rising sun (mid-right at horizon) */}
          <circle cx="1330" cy="500" r="150" fill="url(#risingSun)" />
          <circle cx="1330" cy="500" r="52" fill="#E08A4A" opacity="0.95" />

          {/* Far mountains */}
          <path
            d="M0 560 L120 500 L240 550 L360 470 L480 540 L620 490 L760 550 L900 480 L1040 540 L1180 500 L1320 555 L1460 505 L1600 555 L1600 700 L0 700 Z"
            fill="#A9C0AD" opacity="0.9"
          />
          {/* Mid mountains */}
          <path
            d="M0 620 L120 585 L260 610 L400 560 L560 600 L720 570 L860 615 L1000 570 L1160 610 L1320 580 L1460 615 L1600 590 L1600 720 L0 720 Z"
            fill="#84A490" opacity="0.95"
          />
          {/* Near hills silhouette */}
          <path
            d="M0 690 L120 660 L240 680 L360 640 L480 675 L620 650 L760 680 L900 645 L1040 680 L1180 650 L1320 685 L1460 655 L1600 685 L1600 730 L0 730 Z"
            fill="#5E826B"
          />

          {/* Water reflection */}
          <rect y="700" width="1600" height="200" fill="url(#waterGrad)" />
          {/* Water shimmer bands */}
          {Array.from({ length: 8 }).map((_, i) => (
            <rect key={i} x={0} y={720 + i * 22} width="1600" height="1" fill="#1E3A2F" opacity={0.05 + (i % 3) * 0.02} />
          ))}

          {/* Trees silhouette on shore */}
          <g fill="#3F6152">
            {Array.from({ length: 40 }).map((_, i) => {
              const x = i * 42 + (i % 3) * 6;
              const h = 18 + ((i * 7) % 14);
              return <path key={i} d={`M${x} 700 L${x + 5} ${700 - h} L${x + 10} 700 Z`} />;
            })}
          </g>
        </svg>
      </div>

      {/* Soft horizon blend into the page */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#FDF6E7]/50 pointer-events-none" />

      {/* --- Content --- */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="text-center">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display font-light tracking-tight leading-[0.98] text-[#241209] text-[10vw] md:text-[6.5vw] lg:text-[5.4vw] max-w-[1200px] mx-auto"
          >
            A LIVING ARCHIVE OF OUR<br />CHANGING PLANET.
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-8 md:mt-10 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-[#4A3B2C]"
          >
            What did you notice today? Take a photograph, describe what you saw
            in a sentence, or explore observations reported near you.
          </motion.p>

          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-6 flex justify-center"
          >
            <ListenTranslateBar
              text="NaturePulse — a living archive of our changing planet. What did you notice today? Take a photograph, describe what you saw in a sentence, or explore observations reported near you. Science, weather, plants, species and farmers — one observation network."
              testidPrefix="home-tools" />
          </motion.div>

          {/* Three-action hero — the primary decision surface for new visitors */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 max-w-[1100px] mx-auto"
            data-testid="hero-three-actions"
          >
            <a
              href="#identify"
              data-testid="hero-action-photo"
              className="group text-left rounded-2xl border border-black/10 bg-white/55 hover:bg-white/85 backdrop-blur-sm p-5 md:p-6 transition-colors"
            >
              <div className="flex items-center gap-2 text-[#E4A15C]">
                <Camera size={22} weight="fill" />
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase">01 / Photo</span>
              </div>
              <div className="mt-3 font-serif-display text-xl md:text-2xl leading-tight text-[#241209]">
                Take a Photo
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-[#5B4A38]">
                Identify and document a plant, animal, insect, sky or landscape.
              </p>
              <div className="mt-4 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C] opacity-0 group-hover:opacity-100 transition-opacity">
                Upload &amp; identify →
              </div>
            </a>

            <a
              href="#identify"
              data-testid="hero-action-describe"
              className="group text-left rounded-2xl border border-black/10 bg-white/55 hover:bg-white/85 backdrop-blur-sm p-5 md:p-6 transition-colors"
            >
              <div className="flex items-center gap-2 text-[#E4A15C]">
                <PencilSimpleLine size={22} weight="fill" />
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase">02 / Describe</span>
              </div>
              <div className="mt-3 font-serif-display text-xl md:text-2xl leading-tight text-[#241209]">
                Describe What I Saw
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-[#5B4A38]">
                Share an observation in one sentence — even without a photograph.
              </p>
              <div className="mt-4 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C] opacity-0 group-hover:opacity-100 transition-opacity">
                Tell your story →
              </div>
            </a>

            <Link
              to="/civic-reports"
              data-testid="hero-action-nearby"
              className="group text-left rounded-2xl border border-black/10 bg-white/55 hover:bg-white/85 backdrop-blur-sm p-5 md:p-6 transition-colors"
            >
              <div className="flex items-center gap-2 text-[#E4A15C]">
                <MapTrifold size={22} weight="fill" />
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase">03 / Nearby</span>
              </div>
              <div className="mt-3 font-serif-display text-xl md:text-2xl leading-tight text-[#241209]">
                Explore Near Me
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-[#5B4A38]">
                See nature, weather and environmental changes reported nearby.
              </p>
              <div className="mt-4 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E4A15C] opacity-0 group-hover:opacity-100 transition-opacity">
                Open the map →
              </div>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Footer strip inside hero */}
      <div className="relative z-10 border-t border-black/10 bg-white/70 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center justify-between gap-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#5B4A38]">
          <span>NaturePulse™ © 2026 · A Social Pulse Endeavor</span>
          <span>{date} · {time} · Portland, OR</span>
        </div>
      </div>
    </section>
  );
}
